import { useCallback, useEffect, useState } from "react";
import type { SlideProps } from "@/engine/types";
import { chapterMeta } from "@/engine/chapters";
import { AnimatePresence, motion } from "motion/react";
import { FlaskConical, Grid3x3, MousePointerClick } from "lucide-react";
import {
  Badge,
  CodeBlock,
  ExplainerPanel,
  GradientText,
  Reveal,
  SlideShell,
} from "@/ui";
import { ProductPageMock } from "./components/ProductPageMock";
import { ALT_CODE, HOTSPOTS, SCHEMA_CODE } from "./components/hotspots";
import type { HotspotId, HotspotInfo } from "./components/hotspots";

/* ── mini Google-style result card for the meta-description panel ── */

function SerpPreview() {
  return (
    <div className="mb-6 rounded-xl border border-white/10 bg-ink-900/90 p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 font-display text-base font-bold text-white">
          S
        </span>
        <div className="min-w-0">
          <p className="text-xl leading-tight text-white/80">Stride</p>
          <p className="truncate font-mono text-xl leading-tight text-emerald-300/80">
            stride.example › running › stability-shoes
          </p>
        </div>
      </div>
      <p className="mt-2.5 text-2xl leading-snug text-sky-400">
        Stability Runner — Flat Feet | Stride
      </p>
      <p className="mt-1.5 text-xl leading-snug text-white/70">
        A stability running shoe with firm arch support, built for flat feet.
        $129 with free returns — find your size at Stride.
      </p>
    </div>
  );
}

const [ACCENT_FROM, ACCENT_TO] = chapterMeta(2).accent;

export function OnPageAnatomySlide({ active }: SlideProps) {
  const [selected, setSelected] = useState<HotspotId | null>(null);
  const [visited, setVisited] = useState<ReadonlySet<HotspotId>>(() => new Set());
  const [showAll, setShowAll] = useState(false);
  // Keeps the last-opened hotspot rendered through the panel's exit animation.
  const [display, setDisplay] = useState<HotspotInfo | null>(null);

  const select = useCallback((id: HotspotId) => {
    setSelected(id);
    setShowAll(false);
    setVisited((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    const info = HOTSPOTS.find((h) => h.id === selected);
    if (info) setDisplay(info);
  }, [selected]);

  // Reset transient UI whenever the slide leaves the spotlight (overview open).
  useEffect(() => {
    if (!active) {
      setSelected(null);
      setShowAll(false);
    }
  }, [active]);

  // Number keys 1-9 and 0 (=10) jump straight to a hotspot.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!/^[0-9]$/.test(e.key)) return;
      e.preventDefault();
      const idx = e.key === "0" ? 9 : Number(e.key) - 1;
      const info = HOTSPOTS[idx];
      if (info) select(info.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, select]);

  const panelOpen = selected !== null;

  return (
    <SlideShell chapter={2} kicker="Demo · Everything you control on the URL">
      <div className="relative flex h-full min-h-0 w-full flex-col">
        {/* controls row */}
        <Reveal
          active={active}
          className="mb-4 flex shrink-0 items-center justify-between gap-6"
        >
          <p className="flex items-center gap-3 text-xl text-white/50">
            <MousePointerClick className="h-6 w-6 text-white/40" />
            Click a pin — or press 1–9 and 0
          </p>
          <div className="flex items-center gap-4">
            <Badge icon={<FlaskConical className="h-5 w-5" />}>
              Demo data — stride.example is invented
            </Badge>
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setShowAll((v) => !v);
              }}
              className="glass flex items-center gap-2.5 px-5 py-2 text-xl font-medium uppercase tracking-widest transition-transform duration-200 hover:scale-105"
              style={
                showAll
                  ? {
                      background: `linear-gradient(90deg, ${ACCENT_FROM}, ${ACCENT_TO})`,
                      color: "#05060b",
                    }
                  : { color: "rgba(255,255,255,0.8)" }
              }
            >
              <Grid3x3 className="h-5 w-5" />
              {showAll ? "Hide all" : "Show all"}
            </button>
          </div>
        </Reveal>

        {/* browser mock — slides left to make room for the explainer panel */}
        <Reveal active={active} delay={0.12} className="min-h-0 flex-1">
          <motion.div
            animate={{ x: panelOpen ? -180 : 0 }}
            transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            className="mx-auto h-full w-full max-w-[1120px]"
          >
            <ProductPageMock
              selected={selected}
              showAll={showAll}
              visited={visited}
              onSelect={select}
            />
          </motion.div>
        </Reveal>

        {/* show-all caption */}
        <AnimatePresence>
          {showAll && (
            <motion.div
              initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: 20, filter: "blur(6px)" }}
              transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
              className="pointer-events-none absolute inset-x-0 -bottom-8 z-20 flex justify-center"
            >
              <p className="glass px-10 py-4 font-display text-3xl text-white/90">
                <GradientText className="font-semibold">
                  Everything you control
                </GradientText>{" "}
                on the URL.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Inside the relative wrapper so it anchors within SlideShell's padding,
            clear of the deck's 64px edge click-zones. */}
        <ExplainerPanel
          open={panelOpen}
          onClose={() => setSelected(null)}
          eyebrow={display ? `Control ${display.n} of 10` : undefined}
          title={display?.title ?? ""}
          footer={
            <p className="text-xl text-white/40">
              Press 1–9 and 0 to jump · Esc to close
            </p>
          }
        >
          {display && (
            <>
              {display.extra === "serpPreview" && <SerpPreview />}
              <p>{display.body}</p>
              {display.extra === "altCode" && (
                <CodeBlock lang="html" code={ALT_CODE} className="mt-6" />
              )}
              {display.extra === "schemaCode" && (
                <CodeBlock lang="json" code={SCHEMA_CODE} className="mt-6" />
              )}
            </>
          )}
        </ExplainerPanel>
      </div>
    </SlideShell>
  );
}
